import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
/* Hooks */
import { useSelector } from 'react-redux';
import useEventListener from 'src/hooks/useEventListener';
/* Utils */
import { getItemTotalWithoutDiscounts } from 'src/containers/CartPage/Parts/ServicesBreakdown/utils';
import { formatDollarAmount } from 'src/utils/formatter/currency';
/* Constants */
import { DESKTOP, ANIMATION_DURATION_DEFAULT } from 'src/containers/AddSkuPageV2/constants';
import { Breakpoints } from 'src/components/HTKit/Grid';
/* Selectors */
import { newItemPriceBreakdownSelector } from 'src/containers/AddSkuPage/selectors';
/* Components && Styles */
import AddSkuV2SkuImage from '../AddSkuV2SkuImage';
import AddSkuV2PriceBreakdown from '../AddSkuV2PriceBreakdown';
import styles from './styles.scss';

const FIXED_CONTAINER_ID = 'AddSkuV2Summary_FIXED_CONTAINER_ID';
/**
 * Display as a fixed position element that sticks to bottom of navbar on scroll. Used for mobile/tablet view
 */
const Fixed = ({ sku, className, hideDesktop = true }) => {
  const breakdown = useSelector(newItemPriceBreakdownSelector);

  /* Hooks */
  const [containerHeight, setContainerHeight] = useState(0);
  const [useAdjustmentAnimation, setUseAdjustmentAnimation] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const toggleShowBreakdown = () => setShowBreakdown(!showBreakdown);

  const containerStyles = cn(styles.fixedContainer, className, {
    'paddingBottom-medium': showBreakdown,
  });

  /* Methods */
  const setHeight = () => {
    /*
      Because Design wants this to be a fixed position header and not sticky we need to insert a div that is the
      same height as our container in order to push the content down to the proper height
    */
    const container = document.getElementById(FIXED_CONTAINER_ID);
    setContainerHeight(container.offsetHeight);
  };

  const makePageAdjustments = () => {
    setHeight();
    if (hideDesktop && window.innerWidth > Breakpoints.LG_GRID) setShowBreakdown(false);
  };

  /* Data */
  const { totalWithoutDiscounts, discountsTotal } = getItemTotalWithoutDiscounts(breakdown);
  const orderTotalAmountText =
    breakdown.amount === 0 ? '$0' : formatDollarAmount({ amount: breakdown.amount });
  const orderTotalLineThroughText = discountsTotal
    ? formatDollarAmount({ amount: totalWithoutDiscounts })
    : null;

  /* Effects */
  useEffect(() => makePageAdjustments(), [breakdown.totalPrice]);
  useEventListener({ eventName: 'resize', handler: makePageAdjustments });

  useEffect(() => {
    /*
      We have to use a timeout because the html elements used to show the adjustments are created/destroyed every time
      the breakdown is shown/hidden. This means the heights will animate every time the breakdown is open/closed. Let's
      only animate the adjustments when adjustments are add/deleted AFTER the breakdown has finished opening.
    */
    const timeoutId = setTimeout(() => {
      if (showBreakdown) setUseAdjustmentAnimation(true);
    }, ANIMATION_DURATION_DEFAULT * 3); // 450ms
    if (!showBreakdown) setUseAdjustmentAnimation(false);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [showBreakdown]);

  return (
    <>
      <aside className={containerStyles}>
        <div className="paddingTop-medium paddingBottom-small2" id={FIXED_CONTAINER_ID}>
          <p className="h4 marginBottom-tiny">{sku.get('name')}</p>
          <div className="flex justifyContentSpaceBetween alignItemsBaseline">
            <div className="flex">
              <p className="p0">
                {orderTotalAmountText || <span style={{ visibility: 'hidden' }}>_</span>}
              </p>
              {orderTotalLineThroughText && (
                <p className="p0 line-through n700 marginLeft-tiny1">{orderTotalLineThroughText}</p>
              )}
            </div>
            <p
              className="p2 text-link -no-decoration text-weight-med"
              onClick={toggleShowBreakdown}
            >
              {showBreakdown ? 'Hide' : 'View'} Breakdown
            </p>
          </div>
        </div>
        {showBreakdown && (
          <AddSkuV2PriceBreakdown
            breakdown={breakdown}
            animationDuration={useAdjustmentAnimation ? ANIMATION_DURATION_DEFAULT : 0}
          />
        )}
      </aside>
      <div style={{ height: `${containerHeight}px` }} className="marginBottom-medium" />
    </>
  );
};

/**
 * Display as a static position element. Used for side-by-side desktop view
 */
const Static = ({ sku, ctaButton, className }) => {
  const breakdown = useSelector(newItemPriceBreakdownSelector);
  return (
    <aside className={cn(styles.staticContainer, className)}>
      <AddSkuV2SkuImage type={DESKTOP} className="marginBottom-medium1" sku={sku} />
      <div className="paddingX-small2 marginBottom-small2">
        <p className="h4 marginBottom-small2">{sku.get('name')}</p>
        <AddSkuV2PriceBreakdown breakdown={breakdown} topBorder />
        {ctaButton}
      </div>
    </aside>
  );
};

const AddSkuV2Summary = { Static, Fixed };

export default AddSkuV2Summary;

Static.displayName = 'AddSkuV2Summary.Static';
Fixed.displayName = 'AddSkuV2Summary.Fixed';

const sharedPropTypes = { sku: PropTypes.object, className: PropTypes.string };
Static.propTypes = { ...sharedPropTypes, ctaButton: PropTypes.element };
Fixed.propTypes = { ...sharedPropTypes, hideDesktop: PropTypes.bool };
